// src/features/medicalSercices/page/SalesPersonForm.tsx

import { useRouter } from '@tanstack/react-router';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import DynamicForm, { type DynamicFormSchema } from '@/common/components/DynamicForm/DynamicForm';
import { CheckICDCode } from '../../../../common/services/realTimeCheckAPI';
import { useTranslation } from 'react-i18next';

export default function IcdForm() {
    const { t } = useTranslation();
    const { state } = useRouter().state.location;
    const record = (state as { record?: Record<string, unknown> })?.record;
    const id = record && record.id !== undefined && record.id !== null
        ? Number(record.id)
        : undefined;

const IcdFormSchema: DynamicFormSchema = {
  layout: 'vertical', // Overall form layout
 // submitButtonText: 'Save User', // Custom text for the submit button
  showResetButton: true, // Show a reset button

  // Define form sections for better organization and complex layouts
    sections: [
        {
            key: 'icd-personal',
            title: 'English',
            type: 'card',
            layout: { type: 'grid', gutter: 24 },
            fields: [
                {
                    name: 'code',
                    label: 'Code',
                    type: 'input',
                    required: true,
                    colSpan: 12,
                    requiredMessage: "Please enter code",
                    validation: [
                        {
                            max: 10,
                            message: 'Code cannot be more than 10 characters long.',
                        },
                        {
                            min: 1,
                            message: 'Code must be at least 1 character long.',
                        },
                        {
                            // ❌ Disallow spaces
                            validator: (_: any, value: string) => {
                                if (!value) return Promise.resolve();

                                if (/\s/.test(value)) {
                                    return Promise.reject(t('Code cannot contain spaces.'));
                                }

                                return Promise.resolve();
                            },
                        },
                        {
                            // ✅ Async validator for checking code uniqueness
                            validator: async (_: any, value: string) => {
                                if (!value) return Promise.resolve();
                                if (value.length < 1) return Promise.resolve();

                                try {
                                    const result = await CheckICDCode(value, id);
                                    if (result.exists) {
                                        return Promise.reject('Code already exists.');
                                    }
                                    return Promise.resolve();
                                } catch (error: any) {
                                    console.error('Error validating Code:', error);
                                    return Promise.reject(t(error.message) || 'Error validating Code.');
                                }
                            },
                        },
                    ],
                },
                {
                    name: 'ShortDescription',
                    label: 'Short Description',
                    type: 'input',
                    required: true,
                    requiredMessage: 'Please enter a ShortDescription.', // Custom required message
                    pattern: 'AlphaNumericAndSomeSpecialCharEngAr',
                    validation: [
                        { min: 1, message: 'ShortDescription must be at least 1 characters long.' },
                        { max: 100, message: 'ShortDescription cannot be more than 100 characters long.' },
                    ],
                    autocomplete: 'false',

                    colSpan: 24, // Takes 12 out of 24 grid units (half width)
                },
                {
                    name: 'Description', label: 'Description', type: 'textarea', required: false, colSpan: 24,
                    pattern: 'AlphaNumericAndSomeSpecialCharEngAr',
                    validation: [
                        { max: 200, message: 'Description cannot be more than 200 characters long.' },
                    ],
                },
            ],
        },
        {
            key: 'icd-contact',
            title: 'Arabic',
            type: 'card',
            layout: { type: 'grid', gutter: 24 },
            fields: [
                {
                    name: 'ShortDescriptionAr',
                    label: 'Short Description',
                    type: 'input',
                    required: true,
                    requiredMessage: 'Please enter a ShortDescription.', // Custom required message
                    pattern: 'AlphabetsWithSpaceAr',
                    validation: [
                        { min: 1, message: 'ShortDescription must be at least 1 characters long.' },
                        { max: 100, message: 'ShortDescription cannot be more than 100 characters long.' },
                    ],
                    autocomplete: 'false',

                    colSpan: 24, // Takes 12 out of 24 grid units (half width)
                },
                {
                    name: 'DescriptionAr', label: 'Description', type: 'textarea', required: true, colSpan: 24,
                    requiredMessage: 'Please enter a ShortDescription.', // Custom required message
                    pattern: 'AlphabetsWithSpaceAr',
                    validation: [
                        { max: 200, message: 'Description cannot be more than 200 characters long.' },
                    ],
                },
                
            ],
        },
    ],
};

  return (
    <DynamicForm
          schema={IcdFormSchema}
          submitApiUrl={API_ENDPOINTS.ICD.SAVE_ICD}
          getByIdApiUrl={API_ENDPOINTS.ICD.GET_ICD_BY_ID}
          updateApiUrl={API_ENDPOINTS.ICD.UPDATE_ICD}
          refreshTableDataKey={QUERY_KEYS.ICD.ICD} 
    />
  );
}
